import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import styles from "./OrderComplete.module.css";
import ProductImage from "../../components/common/ProductImage/ProductImage";
import { clearCart } from "../../redux/cartReducer";

interface Product {
  id: number;
  quantity: number;
  price: number;
  finalPrice: number;
  product: {
    id: number;
    name: string;
    images: {
      thumbnail: string;
      small: string;
      medium: string;
    }[];
  };
}

interface Group {
  id: number;
  subTotal: number;
  deliveryMethod: string;
  deliveryDate: string;
  deliveryFee: number;
  groupTotal: number;
  products: Product[];
}

export interface OrderState {
  orderId: number;
  generatedOrderId: string;
  createdAt: string;
  email: string;
  customerName: string;
  paymentStatus: "paid" | "pending";
  paymentMethod: "yapePlin" | "money_transfer" | "payme";
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  groups: Group[];
  orderStatus: "processing" | "shipped" | "delivered";
}

const steps = [
  "Pendiente de Pago",
  "Confirmado",
  "Preparando",
  "En camino",
  "Entregado",
];

const OrderComplete: React.FC = () => {
  const dispatch = useDispatch();
  const { state } = useLocation();
  // Try to get order data from navigation state, fallback to sessionStorage
  let orderState = state as OrderState | null;

  if (!orderState) {
    const stored = sessionStorage.getItem("orderSuccessData");
    if (stored) {
      try {
        orderState = JSON.parse(stored);
      } catch {
        orderState = null;
      }
    }
  }

  // Always scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Always call useEffect at the top level
  useEffect(() => {
    if (orderState) {
      dispatch(clearCart());
      localStorage.removeItem("cartId");
    }
  }, [dispatch, orderState]);

  // If still no orderState, show error or redirect
  if (!orderState) {
    return (
      <div className={styles.container}>
        <div className={styles.headerWrapper}>
          <h1>Orden no encontrada</h1>
          <p>
            No se encontró información de la orden. Por favor, revisa tu correo
            o vuelve a intentarlo desde el historial de pedidos.
          </p>
        </div>
      </div>
    );
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDeliveryMethod = (method: string) => {
    switch (method.toLowerCase()) {
      case "standard":
        return "Standard";
      case "rapido":
        return "Rápido";
      case "express":
        return "Express";
      default:
        return method;
    }
  };

  const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };
    return new Date(date).toLocaleDateString("es-ES", options);
  };

  const getActiveStepIndex = () => {
    // Special logic for card payments ("payme")
    if (orderState!.paymentMethod === "payme") {
      if (orderState!.paymentStatus === "pending") return 0; // "Pendiente de Pago"
      if (orderState!.paymentStatus === "paid") return 2; // "Preparando"
      if (orderState!.orderStatus === "shipped") return 3;
      if (orderState!.orderStatus === "delivered") return 4;
      return 2; // Default to "Preparando"
    }
    // Default logic for other payment methods
    if (orderState!.paymentStatus === "pending") return 0;
    if (orderState!.paymentStatus === "paid") return 1;
    if (orderState!.orderStatus === "shipped") return 3;
    if (orderState!.orderStatus === "delivered") return 4;
    return 2; // "Preparando" is default after paid
  };

  const renderStepper = () => {
    const activeStepIndex = getActiveStepIndex();

    return (
      <div className={styles.stepper}>
        {steps.map((step, index) => {
          const isCompleted = index < activeStepIndex;
          const isActive = index === activeStepIndex;

          return (
            <div key={index} className={styles.step}>
              <div
                className={`${styles.circle} ${
                  isCompleted ? styles.completed : ""
                } ${isActive ? styles.active : ""}`}
              >
                {index + 1}
              </div>
              <div className={styles.stepLabel}>{step}</div>
              {index !== steps.length - 1 && (
                <div
                  className={`${styles.connector} ${
                    isCompleted ? styles.completedConnector : ""
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPaymentInstructions = () => {
    if (orderState!.paymentMethod === "yapePlin") {
      return (
        <div className={styles.paymentInstructions}>
          <h3>Instrucciones de Pago - Yape / Plin</h3>
          <div className={styles.qrSection}>
            <p>Escanea el código QR para realizar el pago</p>
            <img
              src={`${process.env.REACT_APP_IMAGE_FOLDER}/evefant_yape_QR_b0bef0eaad.webp`}
              alt="Yape QR Code"
              className={styles.qrCode}
            />
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>Celular:</span>
              <span className={styles.accountValue}>906 869 576</span>
              <button
                className={styles.copyButton}
                onClick={() => handleCopy("906869576")}
                title="Copiar número"
              >
                <ContentCopyIcon />
              </button>
            </div>
            <p>Titular: Evefant SAC</p>
          </div>
          {renderContactSection()}
        </div>
      );
    }

    if (orderState!.paymentMethod === "money_transfer") {
      return (
        <div className={styles.paymentInstructions}>
          <h3>Instrucciones de Pago - Transferencia Bancaria</h3>
          <div className={styles.bankDetails}>
            <div className={styles.bankHeader}>
              <img
                src={`${process.env.REACT_APP_IMAGE_FOLDER}/Logo_bcp_vector_58cf4828f2.svg`}
                alt="BCP"
                className={styles.bankLogo}
              />
              <h4>Banco de Crédito BCP</h4>
            </div>
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>Nro. Cuenta:</span>
              <div className={styles.accountValueWrapper}>
                <span className={styles.accountValue}>194-9074-6492-0-20</span>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy("19490746492020")}
                  title="Copiar número de cuenta"
                >
                  <ContentCopyIcon />
                </button>
              </div>
            </div>
            <div className={styles.accountDetail}>
              <span className={styles.accountLabel}>CCI:</span>
              <div className={styles.accountValueWrapper}>
                <span className={styles.accountValue}>
                  002-194-19074-6492-0-2091
                </span>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy("00219419074649202091")}
                  title="Copiar CCI"
                >
                  <ContentCopyIcon />
                </button>
              </div>
            </div>
            <p>Titular: Evefant SAC - Cuenta Corriente</p>
          </div>
          {renderContactSection()}
        </div>
      );
    }
    return null;
  };

  const renderContactSection = () => (
    <div className={styles.contactSection}>
      <p className={styles.contactTitle}>
        Una vez realizado el pago, envíanos el comprobante por cualquiera de
        estos medios:
      </p>
      <div className={styles.contactMethods}>
        <div className={styles.contactMethod}>
          <span className={styles.contactLabel}>
            <WhatsAppIcon /> WhatsApp (906 869 576)
          </span>
          <button
            className={styles.copyButton}
            onClick={() => handleCopy("906869576")}
            title="Copiar número"
          >
            <ContentCopyIcon />
          </button>
        </div>
        <div className={styles.contactMethod}>
          <span className={styles.contactLabel}>
            <EmailIcon /> Email (ventas@evefant.com)
          </span>
          <button
            className={styles.copyButton}
            onClick={() => handleCopy("ventas@evefant.com")}
            title="Copiar email"
          >
            <ContentCopyIcon />
          </button>
        </div>
      </div>
    </div>
  );

  const renderGroupSummary = (group: Group, index: number) => (
    <div key={group.id} className={styles.groupSummary}>
      <div className={styles.groupHeader}>
        <h3>
          {`Paquete ${index + 1} - ${formatDeliveryMethod(
            group.deliveryMethod
          )} - 
          ${formatDate(group.deliveryDate)}`}
        </h3>
      </div>

      <div className={styles.groupContent}>
        {group.products.map((product) => (
          <div key={product.id} className={styles.product}>
            <ProductImage
              src={`${product.product.images[0]?.thumbnail}`}
              alt={product.product.name}
              className={styles.productImage}
            />
            <div className={styles.productInfo}>
              <span className={styles.productName}>{product.product.name}</span>
              <span className={styles.productDetails}>
                Cantidad: {product.quantity} x S/ {product.price.toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.groupFooter}>
        <div className={styles.footerRow}>
          <span>Subtotal:</span>
          <span>S/ {group.subTotal.toFixed(2)}</span>
        </div>
        <div className={styles.footerRow}>
          <span>Costo de Envío:</span>
          <span>S/ {group.deliveryFee.toFixed(2)}</span>
        </div>
        <div className={styles.footerRow}>
          <span>Total:</span>
          <span>S/ {group.groupTotal.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.headerSection}>
          <div className={styles.successIcon}>
            <img
              src="https://media.evefant.com/green_check_icon_evf_43f85b4028.svg"
              alt="Success Icon"
              width="24"
              height="24"
            />
          </div>
          <h1>¡Orden Confirmada!</h1>
        </div>
        <div className={styles.divider} />
        <div className={styles.notificationSection}>
          <div className={styles.notificationInfo}>
            <h3>
              Enviaremos los cambios de estado de tu orden a{" "}
              <strong>{orderState!.email}</strong>
            </h3>
          </div>
          {renderStepper()}
        </div>
      </div>

      <div className={styles.orderBasicInfo}>
        <div className={styles.card}>
          <p className={styles.orderNumber}>
            Orden #<strong>{orderState!.generatedOrderId}</strong>
          </p>
          <p className={styles.customerName}>
            Cliente: <strong>{orderState!.customerName}</strong>
          </p>
          <p className={styles.paymentMethod}>
            Método de Pago:{" "}
            <strong>
              {orderState!.paymentMethod === "yapePlin"
                ? "Yape / Plin"
                : orderState!.paymentMethod === "money_transfer"
                ? "Transferencia Bancaria"
                : "Tarjeta de Crédito/Débito"}
            </strong>
          </p>
          <p className={styles.paymentStatus}>
            Estado de Pago:{" "}
            <span
              className={`${styles.statusBadge} ${
                orderState!.paymentStatus === "paid"
                  ? styles.statusPaid
                  : styles.statusPending
              }`}
            >
              {orderState!.paymentStatus === "paid" ? "Pagado" : "Pendiente"}
            </span>
          </p>
          <p className={styles.totalAmount}>
            Total a Pagar:{" "}
            <strong className={styles.totalAmountHighlight}>
              S/ {orderState!.finalTotal.toFixed(2)}
            </strong>
          </p>
        </div>

        {orderState!.paymentStatus === "pending" && (
          <div className={styles.card}>{renderPaymentInstructions()}</div>
        )}
      </div>

      <div className={styles.orderSummary}>
        <h2>Detalles de la Orden</h2>
        <div className={styles.groupsContainer}>
          {orderState!.groups.map((group, index) =>
            renderGroupSummary(group, index)
          )}
        </div>
        <div className={styles.orderTotals}>
          <div className={styles.totalRow}>
            <span>Subtotal:</span>
            <span>S/ {orderState!.totalPrice.toFixed(2)}</span>
          </div>
          <div className={styles.totalRow}>
            <span>Total Envío:</span>
            <span>S/ {orderState!.totalDeliveryFee.toFixed(2)}</span>
          </div>
          <div className={`${styles.totalRow} ${styles.finalTotal}`}>
            <span>Total a Pagar:</span>
            <span>S/ {orderState!.finalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderComplete;
